/**
 * An organization's social links, as a form holds them and as the API stores
 * them.
 *
 * The backend stores one open object: nine known platforms are typed, and any
 * other key of lower case letters, digits and underscores is allowed so a new
 * network does not need a database change. Every value must be an http or https
 * address of at most 500 characters.
 *
 * An object is the wrong shape to edit. A person adds a row, picks a platform
 * and types an address, and may have a half-filled row on screen; an object
 * cannot hold a row whose key has not been chosen yet. So the form holds rows
 * and converts at each edge.
 */
import { z } from 'zod';

/** The platforms the backend names. Anything else is a custom key. */
export const KNOWN_PLATFORMS = [
  { value: 'twitter', label: 'X (Twitter)' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'bluesky', label: 'Bluesky' },
  { value: 'mastodon', label: 'Mastodon' },
  { value: 'threads', label: 'Threads' },
] as const;

/** What the platform select holds when the key is typed by hand. */
export const CUSTOM_PLATFORM = '__custom__';

const KNOWN_VALUES: string[] = KNOWN_PLATFORMS.map((option) => option.value);

/** One row of the editor. */
export interface SocialLinkRow {
  /** A known platform's value, or CUSTOM_PLATFORM. */
  platform: string;
  /** Only used when platform is CUSTOM_PLATFORM. */
  custom: string;
  url: string;
}

/** The key a row will be stored under: the platform, or the custom key. */
export function keyFor(row: SocialLinkRow): string {
  return row.platform === CUSTOM_PLATFORM ? row.custom.trim() : row.platform;
}

export const socialLinkRowSchema = z
  .object({
    platform: z.string().min(1, 'Choose a platform'),
    custom: z.string().trim().default(''),
    url: z
      .string()
      .trim()
      .min(1, 'Enter the address')
      .max(500, 'Use 500 characters or fewer')
      .refine(
        (value) => /^https?:\/\//.test(value),
        'Enter a full web address, starting with http:// or https://',
      ),
  })
  /*
   * Checked on the row rather than on the field, because the rule is about two
   * fields at once: the custom key only exists, and only has to be valid, when
   * the platform is "Other".
   */
  .refine((row) => row.platform !== CUSTOM_PLATFORM || row.custom.trim().length > 0, {
    message: 'Name the platform',
    path: ['custom'],
  })
  .refine(
    (row) =>
      row.platform !== CUSTOM_PLATFORM || /^[a-z0-9_]{1,30}$/.test(row.custom.trim()),
    {
      message: 'Use lower case letters, digits and underscores, up to 30 characters',
      path: ['custom'],
    },
  );

/**
 * The whole list, with the one rule that spans rows.
 *
 * A repeated platform is marked on the later row, not the first: the first one
 * was there and was fine, and it is the row just added that has to change.
 */
export const socialLinksSchema = z.array(socialLinkRowSchema).superRefine((rows, ctx) => {
  const seen = new Set<string>();
  rows.forEach((row, index) => {
    const key = keyFor(row);
    if (!key) return;
    if (seen.has(key)) {
      ctx.addIssue({
        code: 'custom',
        message: 'This platform is already listed',
        path: [index, row.platform === CUSTOM_PLATFORM ? 'custom' : 'platform'],
      });
    }
    seen.add(key);
  });
});

/**
 * Turn what the API stored into rows.
 *
 * Order is the object's own, which is the order the backend returned. Nothing
 * sorts them: a person who put their main channel first should see it first.
 */
export function toSocialLinkRows(
  stored: Record<string, unknown> | null | undefined,
): SocialLinkRow[] {
  if (!stored) return [];

  return Object.entries(stored)
    .filter(([, value]) => typeof value === 'string' && value !== '')
    .map(([key, value]) => ({
      platform: KNOWN_VALUES.includes(key) ? key : CUSTOM_PLATFORM,
      custom: KNOWN_VALUES.includes(key) ? '' : key,
      url: value as string,
    }));
}

/**
 * Turn rows back into the one object the API stores.
 *
 * Sent whole, always. A link that was removed is simply absent from the object,
 * which is how the backend is told it is gone; sending only the rows that
 * changed would leave a deleted link in place.
 */
export function toSocialLinksPayload(rows: SocialLinkRow[]): Record<string, string> {
  const payload: Record<string, string> = {};
  for (const row of rows) {
    const key = keyFor(row);
    const url = row.url.trim();
    if (key && url) payload[key] = url;
  }
  return payload;
}
